<template>
  <div>
    <el-upload class="img-list-preview" v-if="viewType === 'img'" :file-list="formatFileList" list-type="picture-card" v-bind="$attrs">
      <div slot="file" slot-scope="{file}">
        <el-image
          class="el-upload-list__item-thumbnail"
          :src="file.url"
          fit="fill"
          :preview-src-list="formatFileList.map(item => item.url)"
        />
      </div>
    </el-upload>
    <el-upload v-else :class="['file-upload', { readonly }]" :multiple="multiple" :file-list="formatFileList"
               :accept="accept" :action="$apis.common.uploadUrl" :limit="limit" :before-upload="beforeUpload"
               :on-success="onSuccess" :on-change="onUploadChange" :on-exceed="onExceed" :on-remove="onRemove" v-bind="$attrs">
      <template v-if="canUpload">
        <el-button type="text" icon="el-icon-plus" v-if="readonly"
                   size="small" @click.stop="handleAddClick">添加附件</el-button>
        <el-button type="text" icon="el-icon-plus" v-else size="small">点击上传</el-button>
      </template>
      <template #file="{ file, index }">
        <div class="file-list-item" v-if="['uploading', 'success', 'ready'].includes(file.status)">
          <i class="el-icon-document"></i>
          <span class="name">{{ file.name }}</span>
          <i class="el-icon-delete close" v-if="file.status === 'success' && !readonly && hasRemove"
             @click="onRemove(file, index)"></i>
          <i class="el-icon-view download"
             v-if="file.status === 'success' && preview && ['jpg', 'png', 'jpeg', 'gif'].includes(getFileType(file.name))"
             @click="onPreview(file)"></i>
          <i class="el-icon-download download" v-if="file.status === 'success' && download"
             @click="onDownload(file)"></i>
          <!-- 新附件也支持更新按钮 -->
          <i class="el-icon-refresh-right download" v-if="canUpload && file.status === 'success' && update && !uploadDisabled && file.sourceFile && file.sourceFile.fileId"
             @click="onUpdate(file, index)"></i>
          <el-progress v-if="file.status === 'uploading'" :stroke-width="4" :percentage="file.percentage" />
        </div>
      </template>
      <template #tip>
        <slot name="tip">
          <div v-if="canUpload" class="el-upload__tip">支持{{ accept }}的文件，单个文件不超过 {{ maxSize }}MB，最多可上传 {{ limit }} 个文件
          </div>
          <div class="no-data" v-else-if="formatFileList.length === 0">
            <span>暂无附件</span>
          </div>
        </slot>
      </template>
    </el-upload>

    <el-dialog class="global-dialog" :append-to-body="true" :visible.sync="dialogVisible2" :title="dialogTitle" width="500">
      <el-form class="global-form" style="padding: 30px 40px 20px 20px;">
        <el-form-item label="当前附件" v-if="!showAddDialog">
          {{ currentFileName }}
        </el-form-item>
        <el-form-item label="附件">
          <file-upload v-model="formData.fileUrl" :multiple="false" :limit="1"></file-upload>
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dialogVisible2 = false">取消</el-button>
          <el-button type="primary" @click="submit(null)">确定</el-button>
        </div>
      </template>
    </el-dialog>

    <image-viewer :visible.sync="dialogVisible" :initial-index="currentImageIndex" :url-list="imageList"></image-viewer>
  </div>
</template>

<script>
import { deepClone } from '../utils/utils'
import { attachmentFiles, isAttachmentPlaceholder } from '../utils/attachments'

export default {
  name: 'FileUpload',
  props: {
    viewType: {
      type: String,
      default: 'list'
    },
    value: {
      type: Array,
      default() {
        return []
      }
    },
    update: {
      type: Boolean,
      default: true
    },
    accept: {
      type: String,
      default: '.pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.jpg,.png,.jpeg,.gif'
    },
    multiple: {
      type: Boolean,
      default: true
    },
    limit: {
      type: Number,
      default: 10
    },
    maxSize: {
      type: Number,
      default: 50
    },
    download: {
      type: Boolean,
      default: true
    },
    preview: {
      type: Boolean,
      default: true
    },
    readonly: {
      type: Boolean,
      default: false
    },
    hasAdd: {
      type: Boolean,
      default: false
    },
    hasRemove: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      currentImageIndex: 0,
      uploadingFiles: [],
      dialogVisible: false,
      dialogVisible2: false,
      showAddDialog: false, // 标记：新增附件弹窗
      formData: {
        fileId: '',
        fileUrl: []
      },
      currentFileName: ''
    }
  },
  computed: {
    buttonList() {
      return this.$store.getters.getMenuByCode('ZHGL')
    },
    // 特殊处理，全局上传附件按钮权限，专门定制，所以放在了账号权限菜单里面，只要有这个权限，就允许所有页面的附件都可以上传和修改
    hasXGFJ() {
      return this.buttonList.includes('XGFJ')
    },
    canUpload() { return !this.readonly || ((this.hasAdd || this.hasXGFJ) && this.attachmentRelation.objectId) },
    uploadDisabled() { return this.$attrs.disabled !== undefined && this.$attrs.disabled !== false },
    hasAttachmentPlaceholder() {
      const files = this.value || []
      return files.length === 1 && isAttachmentPlaceholder(files[0])
    },
    attachmentRelation() {
      const first = (this.value || []).find(file => file && file.objectId) || {}
      return {
        objectId: first.objectId || '',
        objectField: first.objectField || ''
      }
    },
    formatFileList() {
      const files = attachmentFiles(this.value)
      return files.map(item => {
        return {
          name: item.fileName,
          sourceFile: item,
          url: (item.filePrefix || '') + (item.fileUrl || '')
        }
      }).concat(this.uploadingFiles)
    },
    imageList() {
      return this.formatFileList.filter(item => {
        return ['jpg', 'png', 'jpeg', 'gif'].includes(this.getFileType(item.name))
      })
    },
    dialogTitle() {
      return this.showAddDialog ? '添加附件' : '更新附件'
    }
  },
  watch: {
    value: { immediate: true, handler: 'normalizeFormAttachments' },
    readonly: 'normalizeFormAttachments',
    hasAdd: 'normalizeFormAttachments'
  },
  created() {
  },
  methods: {
    // 拦截上传按钮点击：readonly+canUpload 走弹窗新增，否则原生上传
    handleAddClick() {
      if (this.readonly && this.canUpload) {
        this.onAddFile()
      }
    },
    // 打开新增附件弹窗
    onAddFile() {
      this.showAddDialog = true
      this.formData.fileId = ''
      this.formData.fileUrl = []
      this.currentFileName = ''
      this.dialogVisible2 = true
    },
    normalizeFormAttachments() {
      if (this.readonly || this.hasAdd || !Array.isArray(this.value)) return
      const files = attachmentFiles(this.value)
      if (files.length !== this.value.length) this.$emit('input', files)
    },
    onUploadChange(file, files) {
      this.uploadingFiles = files.filter(item => ['ready', 'uploading'].includes(item.status))
    },
    appendFile(file) {
      const files = attachmentFiles(this.value).concat(file)
      console.log('appendFile', files, file)
      if (Array.isArray(this.value)) this.value.splice(0, this.value.length, ...files)
      else this.$emit('input', files)
    },
    onRemove(file, files) {
      if (Array.isArray(files)) this.onUploadChange(file, files)
      if (!Array.isArray(files) && (this.readonly || this.uploadDisabled)) return
      const index = (this.value || []).indexOf(file.sourceFile)
      if (index >= 0) this.value.splice(index, 1)
    },
    onUpdate(file) {
      if (this.uploadDisabled) return
      const data = file.sourceFile
      if (!data || !data.fileId) return
      this.showAddDialog = false
      this.formData.fileId = data.fileId
      this.currentFileName = file.name || '未知'
      this.formData.fileUrl = []
      this.dialogVisible2 = true
    },
    async submit(data) {
      if (this.uploadDisabled) return false
      if (!data && !this.formData.fileUrl.length) {
        this.$message({ type: 'error', message: '请上传文件' })
        return
      }
      const formData = deepClone(this.formData)
      let formData2 = {}
      // 新增附件分支
      if (this.showAddDialog) {
        if (!this.attachmentRelation.objectId) {
          this.$message({ type: 'error', message: '缺少附件关联ID，请刷新后重试' })
          return false
        }
        formData2 = {
          ...formData.fileUrl[0],
          ...this.attachmentRelation
        }
        delete formData2.fileId
        try {
          const data = await this.$axios.post(this.$apis.common.updateUrl, formData2)
          this.appendFile(data)
          this.dialogVisible2 = false
          // this.$emit('saved', formData2)
          return true
        } catch (err) {
          return false
        }
      } else if (this.hasAdd && data) {
        if (!this.attachmentRelation.objectId) {
          this.$message({ type: 'error', message: '缺少附件关联ID，请刷新后重试' })
          return false
        }
        formData2 = { ...data, ...this.attachmentRelation }
        delete formData2.fileId
        try {
          const data = await this.$axios.post(this.$apis.common.updateUrl, formData2)
          this.appendFile(data)
          this.dialogVisible2 = false
          // this.$emit('saved', formData2)
          return true
        } catch (err) {
          return false
        }
      } else {
        const index = (this.value || []).findIndex(item => item.fileId === this.formData.fileId)
        formData2 = {
          fileId: formData.fileId,
          fileName: formData.fileUrl[0].fileName,
          fileUrl: formData.fileUrl[0].fileUrl
        }
        try {
          await this.$axios.post(this.$apis.common.updateUrl, formData2)
          this.value.splice(index, 1, { ...this.value[index], ...formData.fileUrl[0], fileId: formData.fileId })
          this.dialogVisible2 = false
          // this.$emit('saved', formData2)
          return true
        } catch (err) {
          return false
        }
      }
    },
    onPreview(file) {
      if (file.name && ['jpg', 'png', 'jpeg', 'gif'].includes(this.getFileType(file.name))) {
        this.currentImageIndex = this.imageList.findIndex(url => url.name === file.name)
        this.dialogVisible = true
      } else {
        window.open(file.url, '_blank')
      }
    },
    onDownload(file) {
      window.location.href = `${file.url}?download=1`
    },
    async onSuccess(res, file, uploadFiles) {
      let saved = false
      if (res.code === 200 && res.data && res.data.fileUrl) {
        if (this.hasAdd) {
          saved = await this.submit(res.data)
        } else {
          this.appendFile(res.data)
          saved = true
        }
      } else {
        this.$message({ type: 'error', message: res.message || '上传失败，请重试' })
      }
      if (!saved) {
        const fileIndex = uploadFiles.findIndex(item => item.uid === file.uid)
        if (fileIndex >= 0) uploadFiles.splice(fileIndex, 1)
      }
    },
    onExceed(files) {
      this.$message({
        type: 'error',
        message: `本次选择${files.length}个，已超出最大上传个数，请重新选择`
      })
    },
    beforeUpload(file) {
      // readonly模式下不走原生上传，弹窗模式接管
      if (this.readonly && this.canUpload) return false
      if (!this.canUpload || this.uploadDisabled) return false
      if (this.hasAdd && !this.attachmentRelation.objectId) {
        this.$message({ type: 'error', message: '缺少附件关联ID，请刷新后重试' })
        return false
      }
      const fileType = this.getFileType(file.name)
      if (!this.accept.includes(fileType)) {
        this.$message({ type: 'error', message: `${file.name}文件格式错误` })
        return false
      }
      if (file.size > this.maxSize * 1024 * 1024) {
        this.$message({ type: 'error', message: `${file.name}超过文件的最大大小` })
        return false
      }
      return true
    },
    getFileType(name) {
      if (!name) {
        return ''
      }
      const suffix = name.substring(name.lastIndexOf('.') + 1)
      return suffix.toLowerCase()
    }
  }
}
</script>

<style scoped lang="scss">
.img-list-preview {
  ::v-deep {
    .el-upload--picture-card {
      display: none !important;
    }
  }
}

.file-list-item {
  width: 360px;
  height: 32px;
  display: flex;
  align-items: center;
  padding: 0 6px;
  background-color: #f3f3f3;
  cursor: default;
  border-radius: 4px;

  &:hover {
    background-color: #e8e8e8;
  }

  .name {
    font-size: 14px;
    flex: 1;
    margin-left: 6px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .el-progress {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 3;
    top: initial;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    overflow: hidden;

    ::v-deep {
      .el-progress-bar__outer {
        border-radius: 0;
      }

      .el-progress-bar__inner {
        border-radius: 0;
      }
    }
  }

  .close {
    cursor: pointer;
  }

  .download {
    cursor: pointer;
    margin-left: 8px;
  }
}

.file-upload {
  max-width: 600px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  &.readonly {
    ::v-deep {
      .el-upload-list__item:first-child {
        margin: 0;
      }
    }
  }

  ::v-deep {
    .el-upload-list {
      width: 360px;
    }

    .el-upload {
      display: inline-flex;
    }
  }
}

.el-upload__tip {
  margin-top: 0;
  line-height: 1.5;
}

.no-data {
  font-size: 14px;
}
</style>
